/**
 * Workflow Serialization — JSON save/load with version stamping.
 *
 * Converts Workflow DAG instances to/from a pure-data JSON representation
 * suitable for localStorage, session state, or Gibbs memory system storage.
 *
 * Design principles:
 * - Serialized output is pure data (no functions, no executable code)
 * - Only declarative structure is stored (IDs, names, overrides, conditions)
 * - Runtime-only data (resolved ActionDefs, validation results, adjacency
 *   indexes) is excluded and rebuilt during deserialization
 * - Version field enables forward compatibility
 *
 * @module gibbs/workflow/serialization
 */

import { Workflow } from './Workflow';
import { getAction } from '../registry/index';

/**
 * Current serialization format version.
 * Increment when the format changes in a backward-incompatible way.
 * @type {number}
 */
const CURRENT_VERSION = 2;
const SUPPORTED_VERSIONS = [1, 2];

// ─── Serialize ──────────────────────────────────────────────────────────────

/**
 * @typedef {Object} SerializedNode
 * @property {string} id - Node ID
 * @property {string} actionId - ActionDef ID from registry
 * @property {string} label - Human-readable label
 * @property {Object} paramOverrides - Declarative parameter overrides
 * @property {Object} [paramResolved] - Runtime-resolved parameter values (v2+)
 * @property {{ x: number, y: number }} position - UI position hint
 */

/**
 * @typedef {Object} SerializedEdge
 * @property {string} id - Edge ID
 * @property {string} sourceNodeId - Upstream node ID
 * @property {string} targetNodeId - Downstream node ID
 * @property {string} outputName - Output name from source
 * @property {string} inputName - Input name on target
 * @property {Object|null} condition - Optional branching condition
 */

/**
 * @typedef {Object} SerializedWorkflow
 * @property {number} version - Format version (currently 1)
 * @property {string} [id] - Optional workflow ID
 * @property {string} [name] - Optional workflow name
 * @property {string} [description] - Optional description
 * @property {SerializedNode[]} nodes - All workflow nodes
 * @property {SerializedEdge[]} edges - All workflow edges
 * @property {Object} metadata - Additional metadata
 * @property {string} metadata.createdAt - ISO 8601 timestamp
 * @property {string} [metadata.name] - Duplicate of top-level name for compatibility
 */

/**
 * Serialize a Workflow instance to a plain JSON-compatible object.
 *
 * The output contains only declarative data — no ActionDef objects,
 * no adjacency indexes, no functions. Safe for `JSON.stringify()` and
 * storage in localStorage or the Gibbs memory system.
 *
 * @param {Workflow} workflow - Workflow to serialize
 * @param {Object} [options] - Optional metadata
 * @param {string} [options.id] - Workflow ID
 * @param {string} [options.name] - Workflow name
 * @param {string} [options.description] - Workflow description
 * @returns {SerializedWorkflow}
 * @throws {Error} If workflow is not a Workflow instance
 */
export function toJSON(workflow, options = {}) {
    if (!workflow || typeof workflow.getNodes !== 'function') {
        throw new Error('toJSON() expects a Workflow instance');
    }

    const nodes = workflow.getNodes().map((node) => {
        const serialized = {
            id: node.id,
            actionId: node.actionId,
            label: node.label,
            paramOverrides: node.paramOverrides || {},
            position: node.position || { x: 0, y: 0 },
        };
        // Preserve runtime-resolved values if present
        if (node.paramResolved && Object.keys(node.paramResolved).length > 0) {
            serialized.paramResolved = node.paramResolved;
        }
        return serialized;
    });

    const edges = workflow.getEdges().map((edge) => {
        const serialized = {
            id: edge.id,
            sourceNodeId: edge.sourceNodeId,
            targetNodeId: edge.targetNodeId,
            outputName: edge.outputName,
            inputName: edge.inputName,
            condition: null,
        };

        // Serialize condition if present, stripping any non-serializable fn references
        if (edge.condition) {
            serialized.condition = {
                description: edge.condition.description || '',
                ...(typeof edge.condition.fn === 'string' ? { fn: edge.condition.fn } : {}),
            };
        }

        return serialized;
    });

    return {
        version: CURRENT_VERSION,
        id: options.id || undefined,
        name: options.name || undefined,
        description: options.description || undefined,
        nodes,
        edges,
        metadata: {
            createdAt: new Date().toISOString(),
            ...(options.name ? { name: options.name } : {}),
        },
    };
}

// ─── Deserialize ────────────────────────────────────────────────────────────

/**
 * Reconstruct a Workflow instance from a serialized JSON representation.
 *
 * Validates the format version and structure. Checks each node's actionId
 * against the registry and emits warnings for unresolved actions (but still
 * creates the workflow with available nodes).
 *
 * Adjacency indexes are rebuilt automatically by the Workflow class as
 * nodes and edges are added.
 *
 * @param {SerializedWorkflow | string} json - Serialized workflow (object or JSON string)
 * @returns {{ workflow: Workflow, warnings: string[] }}
 * @throws {Error} If version is unsupported or required fields are missing
 */
export function fromJSON(json) {
    // Accept JSON string or object
    let data;
    if (typeof json === 'string') {
        try {
            data = JSON.parse(json);
        } catch (err) {
            throw new Error(`Failed to parse workflow JSON: ${err.message}`);
        }
    } else {
        data = json;
    }

    // ─── Validate structure ─────────────────────────────────────────────
    if (!data || typeof data !== 'object') {
        throw new Error('Malformed workflow JSON: expected an object');
    }

    if (data.version === undefined || data.version === null) {
        throw new Error('Malformed workflow JSON: missing "version" field');
    }

    if (!SUPPORTED_VERSIONS.includes(data.version)) {
        throw new Error(
            `Unsupported workflow version: ${data.version}. `
            + `This module supports versions ${SUPPORTED_VERSIONS.join(', ')}.`
        );
    }

    if (!Array.isArray(data.nodes)) {
        throw new Error('Malformed workflow JSON: "nodes" must be an array');
    }

    if (!Array.isArray(data.edges)) {
        throw new Error('Malformed workflow JSON: "edges" must be an array');
    }

    // ─── Reconstruct ────────────────────────────────────────────────────
    const workflow = new Workflow();
    const warnings = [];

    // Track old node ID → new node ID mapping (in case IDs collide with
    // the auto-incrementing counter, we keep the original IDs by directly
    // setting them after creation)
    const nodeIdMap = new Map();

    for (const serializedNode of data.nodes) {
        if (!serializedNode.actionId) {
            warnings.push(`Node "${serializedNode.id}" has no actionId — skipped`);
            continue;
        }

        const actionDef = getAction(serializedNode.actionId);
        if (!actionDef) {
            warnings.push(
                `Action "${serializedNode.actionId}" (node "${serializedNode.id}") `
                + 'not found in registry — node created with unresolved action'
            );
        }

        try {
            const node = workflow.addNode(serializedNode.actionId, {
                label: serializedNode.label,
                paramOverrides: serializedNode.paramOverrides || {},
                position: serializedNode.position || { x: 0, y: 0 },
            });

            // Restore runtime-resolved params if present (v2+)
            if (serializedNode.paramResolved) {
                node.paramResolved = serializedNode.paramResolved;
            }

            // Remap: the Workflow auto-generates a new ID, but we need to
            // map the serialized ID to the new one for edge reconstruction.
            nodeIdMap.set(serializedNode.id, node.id);
        } catch (err) {
            warnings.push(
                `Failed to create node for "${serializedNode.actionId}" `
                + `(serialized id "${serializedNode.id}"): ${err.message}`
            );
        }
    }

    // Reconstruct edges using the node ID mapping
    for (const serializedEdge of data.edges) {
        const sourceId = nodeIdMap.get(serializedEdge.sourceNodeId);
        const targetId = nodeIdMap.get(serializedEdge.targetNodeId);

        if (!sourceId) {
            warnings.push(
                `Edge "${serializedEdge.id}": source node "${serializedEdge.sourceNodeId}" `
                + 'not found — edge skipped'
            );
            continue;
        }
        if (!targetId) {
            warnings.push(
                `Edge "${serializedEdge.id}": target node "${serializedEdge.targetNodeId}" `
                + 'not found — edge skipped'
            );
            continue;
        }

        try {
            workflow.addEdge(
                sourceId,
                targetId,
                serializedEdge.outputName || '',
                serializedEdge.inputName || '',
                { condition: serializedEdge.condition || null }
            );
        } catch (err) {
            warnings.push(
                `Failed to create edge "${serializedEdge.id}" `
                + `(${serializedEdge.sourceNodeId} → ${serializedEdge.targetNodeId}): ${err.message}`
            );
        }
    }

    return { workflow, warnings };
}
